import { useEffect, useMemo, useRef, useState } from "react";
import { coreGet, coreMutate } from "../../core-api/api-client";
import { describeCoreError } from "../../core-api/errors";
import { mailboxRoles, type MailboxRole } from "../mailboxes/api";
import "./users.css";

interface Workspace {
  id: string;
  code: string;
  name: string;
  status: string;
}
interface Role {
  id: string;
  workspaceId: string;
  name: string;
  systemManaged: boolean;
  nodePolicyVersion: number;
}
interface Mailbox {
  id: string;
  workspaceId: string;
  emailAddress: string;
  roleType: MailboxRole | null;
  authorizationStatus: string;
  status: string;
  disabledAt: string | null;
  deletedAt: string | null;
}
interface DirectoryUser {
  id: string;
  username: string | null;
  primaryEmail: string | null;
  displayName: string;
  occupation: string;
  administratorType: "NORMAL_ADMIN" | "SUPER_ADMIN";
  memberships: {
    status: string;
    workspace: Workspace;
    roleAssignments: { role: Role }[];
  }[];
  mailboxBindings: { mailbox: Mailbox }[];
}
interface Directory {
  canManage: boolean;
  rolePolicyVersion: number;
  workspaces: Workspace[];
  roles: Role[];
  mailboxes: Mailbox[];
  users: DirectoryUser[];
}
interface Editor {
  user?: DirectoryUser;
  username: string;
  primaryEmail: string;
  name: string;
  occupation: string;
  password: string;
  repeat: string;
  access: Record<string, string[]>;
  mailboxIds: string[];
}
const endpoint = "/auth/admin/user-directory";
const eligible = (mailbox: Mailbox) =>
  mailbox.authorizationStatus === "AUTHORIZED" &&
  mailbox.status === "ACTIVE" &&
  !mailbox.disabledAt &&
  !mailbox.deletedAt;
const mailboxRoleLabel = (mailbox: Mailbox) =>
  mailbox.roleType ? mailboxRoles[mailbox.roleType] : "未分类";

// Rejection sampling avoids modulo bias; every generated password contains all three classes.
export function generateUserPassword(): string {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (;;) {
    let password = "";
    while (password.length < 20) {
      for (const value of crypto.getRandomValues(new Uint8Array(32))) {
        if (value < 248) password += alphabet[value % alphabet.length];
        if (password.length === 20) break;
      }
    }
    if (
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password)
    )
      return password;
  }
}

export function UserManagement({
  currentWorkspaceId = "",
}: {
  currentWorkspaceId?: string;
}) {
  const [directory, setDirectory] = useState<Directory>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [workspaceFilter, setWorkspaceFilter] = useState(currentWorkspaceId);
  const [accountFilter, setAccountFilter] = useState("");
  const [mailboxFilter, setMailboxFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [editor, setEditor] = useState<Editor>();
  const [editorError, setEditorError] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    coreGet<Directory>(endpoint, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setDirectory(data);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(describeCoreError(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (editor) dialog.current?.showModal();
    else dialog.current?.close();
  }, [Boolean(editor)]);
  const filtered = useMemo(
    () =>
      (directory?.users ?? []).filter(
        (user) =>
          (!workspaceFilter ||
            user.memberships.some(
              (m) =>
                m.status === "ACTIVE" && m.workspace.id === workspaceFilter,
            )) &&
          (user.username ?? "")
            .toLowerCase()
            .includes(accountFilter.trim().toLowerCase()) &&
          user.displayName
            .toLowerCase()
            .includes(nameFilter.trim().toLowerCase()) &&
          (!mailboxFilter.trim() ||
            user.mailboxBindings.some(({ mailbox }) =>
              mailbox.emailAddress
                .toLowerCase()
                .includes(mailboxFilter.trim().toLowerCase()),
            )),
      ),
    [directory, workspaceFilter, accountFilter, mailboxFilter, nameFilter],
  );
  async function reload() {
    setLoading(true);
    setError("");
    try {
      setDirectory(await coreGet<Directory>(endpoint));
    } catch (cause) {
      setError(describeCoreError(cause));
    } finally {
      setLoading(false);
    }
  }
  function openEditor(user?: DirectoryUser) {
    setEditorError("");
    setCopied(false);
    const assignableRoleIds = new Set(
      (directory?.roles ?? [])
        .filter(
          (role) =>
            !role.systemManaged &&
            role.nodePolicyVersion === directory?.rolePolicyVersion,
        )
        .map((role) => role.id),
    );
    setEditor({
      user,
      username: user?.username ?? "",
      primaryEmail: user?.primaryEmail ?? "",
      name: user?.displayName ?? "",
      occupation: user?.occupation ?? "",
      password: user ? "" : generateUserPassword(),
      repeat: "",
      access:
        user?.administratorType === "SUPER_ADMIN"
          ? {}
          : Object.fromEntries(
              (user?.memberships ?? [])
                .filter((m) => m.status === "ACTIVE")
                .map((m) => [
                  m.workspace.id,
                  m.roleAssignments
                    .map((a) => a.role.id)
                    .filter((roleId) => assignableRoleIds.has(roleId))
                    .slice(0, 1),
                ]),
            ),
      mailboxIds: user?.mailboxBindings.map((b) => b.mailbox.id) ?? [],
    });
  }
  function closeEditor() {
    if (!busy) {
      setEditor(undefined);
      setEditorError("");
      setCopied(false);
    }
  }
  function patch(values: Partial<Editor>) {
    setEditor((current) => (current ? { ...current, ...values } : current));
  }
  function toggleWorkspace(id: string, checked: boolean) {
    if (!editor) return;
    const access = { ...editor.access };
    if (checked) access[id] = [];
    else delete access[id];
    const allMailboxes = [
      ...(directory?.mailboxes ?? []),
      ...(editor.user?.mailboxBindings.map((b) => b.mailbox) ?? []),
    ];
    patch({
      access,
      mailboxIds: editor.mailboxIds.filter((mailboxId) =>
        allMailboxes.some(
          (m) => m.id === mailboxId && Object.hasOwn(access, m.workspaceId),
        ),
      ),
    });
  }
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!editor || busy) return;
    setEditorError("");
    if (editor.password !== editor.repeat) {
      setEditorError("两次密码不一致，请重新输入重复密码。");
      return;
    }
    const editingSuperAdministrator =
      editor.user?.administratorType === "SUPER_ADMIN";
    if (!editingSuperAdministrator && !Object.keys(editor.access).length) {
      setEditorError("请至少绑定一个工作空间。");
      return;
    }
    if (
      !editingSuperAdministrator &&
      Object.values(editor.access).some((roleIds) => roleIds.length !== 1)
    ) {
      setEditorError("每个工作空间必须选择一个新角色。");
      return;
    }
    setBusy(true);
    try {
      await coreMutate(
        editor.user
          ? `/auth/admin/users/${editor.user.id}`
          : "/auth/admin/users",
        editor.user ? "PUT" : "POST",
        {
          username: editor.username.trim(),
          primaryEmail: editor.primaryEmail.trim(),
          displayName: editor.name.trim(),
          occupation: editor.occupation.trim(),
          ...(editor.password ? { password: editor.password } : {}),
          workspaceAccess: editingSuperAdministrator
            ? []
            : Object.entries(editor.access).map(([workspaceId, roleIds]) => ({
                workspaceId,
                roleIds,
              })),
          mailboxIds: editor.mailboxIds,
          reason: editor.user
            ? "用户管理：修改账号及访问范围"
            : "用户管理：新增账号",
        },
      );
      setNotice(editor.user ? "用户已更新" : "用户已创建");
      setEditor(undefined);
      setCopied(false);
      await reload();
    } catch (cause) {
      setEditorError(describeCoreError(cause));
    } finally {
      setBusy(false);
    }
  }
  const editingSuperAdministrator =
    editor?.user?.administratorType === "SUPER_ADMIN";
  const selectedMailboxes = editor
    ? Array.from(
        new Map(
          [
            ...(directory?.mailboxes ?? []),
            ...(editor.user?.mailboxBindings.map((b) => b.mailbox) ?? []),
          ].map((m) => [m.id, m]),
        ).values(),
      ).filter(
        (m) =>
          editingSuperAdministrator ||
          Object.hasOwn(editor.access, m.workspaceId),
      )
    : [];
  const assignableRoles = (directory?.roles ?? []).filter(
    (role) =>
      !role.systemManaged &&
      role.nodePolicyVersion === directory?.rolePolicyVersion,
  );
  return (
    <section className="user-management" aria-label="用户管理">
      <header className="user-page-header">
        <div>
          <h1>用户管理</h1>
          <p>管理账号、联系邮箱、工作空间角色与已授权发件箱。</p>
        </div>
        {directory?.canManage && (
          <button className="primary" onClick={() => openEditor()}>
            新增用户
          </button>
        )}
      </header>
      <div className="user-content">
        <div className="user-filters">
          <label>
            工作空间
            <select
              value={workspaceFilter}
              onChange={(e) => setWorkspaceFilter(e.target.value)}
            >
              <option value="">全部工作空间</option>
              {directory?.workspaces.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            账号
            <input
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              placeholder="按账号筛选"
            />
          </label>
          <label>
            绑定发件箱
            <input
              value={mailboxFilter}
              onChange={(e) => setMailboxFilter(e.target.value)}
              placeholder="按邮箱地址筛选"
            />
          </label>
          <label>
            姓名
            <input
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="按姓名筛选"
            />
          </label>
          <button
            onClick={() => {
              setWorkspaceFilter(currentWorkspaceId);
              setAccountFilter("");
              setMailboxFilter("");
              setNameFilter("");
            }}
          >
            重置筛选
          </button>
          <button disabled={loading} onClick={() => void reload()}>
            刷新
          </button>
        </div>
        {error && <p role="alert">{error}</p>}
        {notice && <p role="status">{notice}</p>}
        {loading && <p role="status">加载中…</p>}
        <p className="user-muted">共 {filtered.length} 位用户</p>
        <div className="user-table-scroll">
          <table>
            <thead>
              <tr>
                <th>工作空间</th>
                <th>账号</th>
                <th>联系邮箱</th>
                <th>绑定发件箱</th>
                <th>姓名</th>
                <th>是否超级管理员</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-chips">
                      {user.memberships
                        .filter((m) => m.status === "ACTIVE")
                        .map((m) => (
                          <span
                            className="user-chip"
                            key={m.workspace.id}
                            title={m.roleAssignments
                              .map((a) => a.role.name)
                              .join("、")}
                          >
                            {m.workspace.name}
                          </span>
                        ))}
                      {!user.memberships.some((m) => m.status === "ACTIVE") &&
                        "—"}
                    </div>
                  </td>
                  <td>{user.username ?? "—"}</td>
                  <td>{user.primaryEmail ?? "—"}</td>
                  <td>
                    <div className="user-mailbox-list">
                      {user.mailboxBindings.map(({ mailbox }) => (
                        <span key={mailbox.id}>
                          {mailbox.emailAddress} · {mailboxRoleLabel(mailbox)}
                          {!eligible(mailbox) && <small>（授权不可用）</small>}
                        </span>
                      ))}
                      {!user.mailboxBindings.length && "—"}
                    </div>
                  </td>
                  <td>{user.displayName}</td>
                  <td>
                    {user.administratorType === "SUPER_ADMIN" ? "是" : "否"}
                  </td>
                  <td>
                    {directory?.canManage ? (
                      <button onClick={() => openEditor(user)}>修改</button>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {!loading && !filtered.length && (
                <tr>
                  <td colSpan={7} className="user-empty">
                    暂无符合条件的用户
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <dialog
        ref={dialog}
        className="user-modal"
        aria-labelledby="user-editor-title"
        onCancel={(e) => {
          e.preventDefault();
          closeEditor();
        }}
        onClose={closeEditor}
      >
        {editor && (
          <form onSubmit={(e) => void save(e)} autoComplete="off">
            <header>
              <h2 id="user-editor-title">
                {editor.user ? "修改用户" : "新增用户"}
              </h2>
              <button
                type="button"
                aria-label="关闭"
                disabled={busy}
                onClick={closeEditor}
              >
                ×
              </button>
            </header>
            <fieldset disabled={busy} className="user-editor-body">
              <div className="user-fields">
                <label>
                  账号
                  <input
                    required
                    minLength={3}
                    maxLength={64}
                    disabled={editingSuperAdministrator}
                    value={editor.username}
                    onChange={(e) => patch({ username: e.target.value })}
                  />
                  {editingSuperAdministrator && (
                    <small className="user-muted">
                      超级管理员登录账号为受保护身份，不支持改名。
                    </small>
                  )}
                </label>
                <label>
                  联系邮箱
                  <input
                    type="email"
                    maxLength={320}
                    placeholder="用于团队分发邮件（选填）"
                    value={editor.primaryEmail}
                    onChange={(e) => patch({ primaryEmail: e.target.value })}
                  />
                </label>
                <label>
                  姓名
                  <input
                    required
                    maxLength={160}
                    value={editor.name}
                    onChange={(e) => patch({ name: e.target.value })}
                  />
                </label>
                <label>
                  职业
                  <input
                    type="text"
                    maxLength={160}
                    placeholder="请输入职业（选填）"
                    value={editor.occupation}
                    onChange={(e) => patch({ occupation: e.target.value })}
                  />
                </label>
              </div>
              <div className="user-password-panel">
                <div className="user-password-heading">
                  <strong>{editor.user ? "重置密码" : "自动生成密码"}</strong>
                  <button
                    type="button"
                    onClick={() => {
                      patch({ password: generateUserPassword(), repeat: "" });
                      setCopied(false);
                    }}
                  >
                    {editor.password ? "重新生成" : "生成新密码"}
                  </button>
                  {editor.user && editor.password && (
                    <button
                      type="button"
                      onClick={() => patch({ password: "", repeat: "" })}
                    >
                      取消重置
                    </button>
                  )}
                </div>
                {editor.password ? (
                  <>
                    <label>
                      密码（明文，仅本次显示）
                      <input
                        readOnly
                        value={editor.password}
                        autoComplete="new-password"
                        spellCheck={false}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(editor.password);
                          setCopied(true);
                        } catch {
                          setEditorError("复制失败，请选中明文密码手动复制。");
                        }
                      }}
                    >
                      {copied ? "已复制" : "复制密码"}
                    </button>
                    <label>
                      重复密码
                      <input
                        required
                        type="password"
                        autoComplete="new-password"
                        value={editor.repeat}
                        onChange={(e) => patch({ repeat: e.target.value })}
                      />
                    </label>
                    <p className="user-muted">
                      20
                      位大小写字母和数字。请复制后交给用户，关闭弹窗后不能再查看此明文密码。
                    </p>
                  </>
                ) : (
                  <p className="user-muted">
                    留空保留原密码。已有密码不可查看，生成新密码后才会重置。
                  </p>
                )}
              </div>
              {editingSuperAdministrator ? (
                <fieldset>
                  <legend>超级管理员权限</legend>
                  <p className="user-muted">
                    超级管理员自动拥有全部工作空间权限，不分配普通角色。可在下方按工作空间绑定发件箱。
                  </p>
                </fieldset>
              ) : (
                <fieldset>
                  <legend>绑定工作空间与角色</legend>
                  <p className="user-muted">
                    勾选工作空间后，为其选择一个新角色；角色仅在所属空间生效。
                  </p>
                  {directory?.workspaces.map((w) => (
                    <div className="workspace-access" key={w.id}>
                      <label className="user-check">
                        <input
                          type="checkbox"
                          checked={Object.hasOwn(editor.access, w.id)}
                          onChange={(e) =>
                            toggleWorkspace(w.id, e.target.checked)
                          }
                        />
                        <strong>{w.name}</strong>
                        <small>{w.code}</small>
                      </label>
                      {Object.hasOwn(editor.access, w.id) && (
                        <div
                          className="workspace-roles"
                          role="group"
                          aria-label={`${w.name}的角色`}
                        >
                          {assignableRoles
                            .filter((r) => r.workspaceId === w.id)
                            .map((r) => {
                              const selected = editor.access[w.id].includes(
                                r.id,
                              );
                              return (
                                <label
                                  className={`user-check workspace-role-option${selected ? " is-selected" : ""}`}
                                  key={r.id}
                                >
                                  <input
                                    type="radio"
                                    name={`workspace-role-${w.id}`}
                                    checked={selected}
                                    onChange={() =>
                                      patch({
                                        access: {
                                          ...editor.access,
                                          [w.id]: [r.id],
                                        },
                                      })
                                    }
                                  />
                                  <span>{r.name}</span>
                                </label>
                              );
                            })}
                          {!assignableRoles.some(
                            (r) => r.workspaceId === w.id,
                          ) && (
                            <span className="user-muted">
                              该空间暂无可分配角色
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {!directory?.workspaces.length && <p>暂无可绑定的工作空间</p>}
                </fieldset>
              )}
              <fieldset>
                <legend>绑定发件箱</legend>
                <p className="user-muted">
                  {editingSuperAdministrator
                    ? "可多选全部工作空间内已授权的有效邮箱。"
                    : "可多选，仅可绑定所选工作空间内已授权的有效邮箱。"}
                </p>
                {selectedMailboxes.map((m) => (
                  <label className="user-check user-mailbox-option" key={m.id}>
                    <input
                      type="checkbox"
                      checked={editor.mailboxIds.includes(m.id)}
                      disabled={
                        !eligible(m) && !editor.mailboxIds.includes(m.id)
                      }
                      onChange={(e) =>
                        patch({
                          mailboxIds: e.target.checked
                            ? [...editor.mailboxIds, m.id]
                            : editor.mailboxIds.filter((id) => id !== m.id),
                        })
                      }
                    />
                    <span>
                      {m.emailAddress} · {mailboxRoleLabel(m)}
                      <small>
                        {
                          directory?.workspaces.find(
                            (w) => w.id === m.workspaceId,
                          )?.name
                        }
                        {!eligible(m) && " · 授权不可用，请取消绑定"}
                      </small>
                    </span>
                  </label>
                ))}
                {!selectedMailboxes.length && (
                  <p className="user-muted">
                    {editingSuperAdministrator
                      ? "当前没有可绑定的已授权发件箱"
                      : Object.keys(editor.access).length
                        ? "所选工作空间暂无已授权发件箱"
                        : "请先选择工作空间"}
                  </p>
                )}
              </fieldset>
            </fieldset>
            {editorError && <p role="alert">{editorError}</p>}
            <footer>
              <button type="button" disabled={busy} onClick={closeEditor}>
                取消
              </button>
              <button className="primary" disabled={busy}>
                {busy ? "保存中…" : "保存用户"}
              </button>
            </footer>
          </form>
        )}
      </dialog>
    </section>
  );
}
